<template>
  <div>
    <TableSkeleton
      v-if="loading"
      :columns="skeletonColumns"
      :lines-per-row="2"
      :show-title="false">
      <template v-slot:above-table>
        <div class="mb-6 flex flex-wrap items-end gap-4">
          <Skeleton width="24rem" height="3rem" />
          <Skeleton width="9rem" height="3rem" />
          <Skeleton width="12rem" height="2.5rem" class="rounded-full" />
          <Skeleton width="7rem" height="2.5rem" class="rounded-full" />
        </div>
      </template>
    </TableSkeleton>
    <div v-else>
      <Teleport to="body">
        <div
          v-if="showError"
          class="fixed top-4 right-4 max-w-lg min-w-80"
          :class="embedded ? 'z-[1200]' : 'z-[1001]'">
          <Alert
            type="error"
            closable
            class="!rounded-xl !bg-red-500/95 !text-sm !font-medium !text-white/90 shadow-lg"
            @close="showError = false">
            <span data-testid="error">{{ error }}</span>
          </Alert>
        </div>
      </Teleport>

      <PolicyDetails
        title="Select Policies"
        scenario="program"
        :consolidated="true"
        :consolidatedContext="true"
        :showExpiredAtStart="false"
        :hideDownload="true"
        :showOnlyMoreFiltersSection="true"
        extraColumnTitle="Selection"
        :preloadedPolicies="policies"
        :preloadedPoliciesLoading="reloadingPolicies"
        :showWithoutProgramToggle="true"
        :showWithoutProgram="true"
        :embedded="embedded"
        :createNewProgram="!isEditMode"
        :key="rerenderKey"
        @policy-details-refresh="refreshPoliciesWithOptions">
        <template v-slot:inner>
          <form @submit.prevent="">
            <div class="form-container">
              <h1 v-if="!embedded">{{ isEditMode ? 'Edit Program' : 'Create New Program' }}</h1>
              <div class="form-controls mt-4 mb-6 flex flex-wrap items-center gap-4">
                <div class="text-entry w-96 shrink-0">
                  <TextField
                    ref="programNameField"
                    class="text-input"
                    v-model="form.programName"
                    placeholder="Program Name"
                    label="Program Name"
                    :error="nameErrors.length > 0"
                    :error-message="nameErrors.join(' ')"
                    hint="Example: Property (Canada)"
                    data-testid="program-name"
                    @update:model-value="
                      () => {
                        v$.form.programName.$touch();
                        duplicateNameError = '';
                        dirty = true;
                      }
                    " />
                </div>
                <div class="text-entry w-36 shrink-0">
                  <TextField
                    class="text-input"
                    v-model="form.year"
                    type="number"
                    placeholder="Program Year"
                    label="Program Year"
                    :error="yearErrors.length > 0"
                    :error-message="yearErrors.join(' ')"
                    hint="Example: 2022"
                    data-testid="program-year"
                    @update:model-value="
                      () => {
                        v$.form.year.$touch();
                        dirty = true;
                      }
                    " />
                </div>
                <!-- An embedded host renders these in its own footer, where they stay
                     reachable instead of scrolling away above the policy table. -->
                <template v-if="!embedded">
                  <AppButton
                    primary
                    :loading="saving"
                    data-testid="save-program"
                    @click="saveProgram()">
                    {{ isEditMode ? 'Save' : 'Create' }} Program
                  </AppButton>
                  <AppButton
                    class="shrink-0 rounded-full"
                    secondary
                    outline
                    data-testid="cancel-program"
                    @click="cancel()">
                    Cancel
                  </AppButton>
                </template>
              </div>
            </div>
          </form>
        </template>

        <template v-slot:extraColumn="{ policy }">
          <div class="flex content-center">
            <input
              type="checkbox"
              :checked="!!selectedPolicies[policy.uniqPolicy]"
              @click="togglePolicy(policy)"
              class="border-opacity-50 border-gray-primary checkbox mx-auto rounded-md border-1 border-solid checkbox-sm checkbox-primary" />
          </div>
        </template>

        <template v-slot:extraColumnHeader>
          <div class="flex content-center">
            <input
              type="checkbox"
              :checked="checkboxState === 'checked'"
              :indeterminate="checkboxState === 'indeterminate'"
              class="border-opacity-50 border-gray-primary checkbox mx-auto rounded-md border-1 border-solid checkbox-sm checkbox-primary"
              @click="toggleAllPolicies" />
          </div>
        </template>

        <template v-slot:filter-bar-left>
          <router-link
            v-if="!embedded"
            :to="`/account/${resolvedAccountId}/programs`"
            color="primary"
            class="back-button flex items-center gap-1 pr-2 transition-all hover:bg-gray-100">
            <div class="flex items-center">
              <ChevronLeft :size="16" />
              Back
            </div>
          </router-link>
        </template>
      </PolicyDetails>
    </div>
  </div>
</template>

<script>
import PolicyDetails from '@/views/policy/PolicyDetails.vue';
import { numeric, required } from '@vuelidate/validators';
import { accountService, referenceService } from '@/services/api';
import { useVuelidate } from '@vuelidate/core';
import { ChevronLeft } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';
import Alert from '@/components/v2/Alert.vue';
import TextField from '@/components/v2/TextField.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import TableSkeleton from '@/components/v2/TableSkeleton.vue';
import { consolidatedColumnLabels } from '@/views/policy/PolicyDetails';
import { IsUserABroker, IsUserAnAdmin } from '@/utilities/AuthUtils';
import store from '@/store';

export default {
  components: { PolicyDetails, ChevronLeft, AppButton, Alert, TextField, Skeleton, TableSkeleton },
  setup() {
    return {
      v$: useVuelidate(),
    };
  },
  data: () => ({
    loading: false,
    reloadingPolicies: false,
    dirty: false,
    rerenderKey: 0,
    saving: false,
    showError: false,
    error: '',
    duplicateNameError: '',
    form: {
      programName: '',
      year: undefined,
    },
    policies: [],
    coverageTypes: [],
    selectedPolicies: {},
    isEditable: true,
    copiedProgramID: undefined,
    showCurrentOnly: true,
    showExternal: false,
    showWithoutProgramOnly: false,
    mergedPrograms: undefined,
    checkboxState: '',
    errorTimeout: null,
  }),
  props: {
    isEditMode: { required: false, type: Boolean, default: false },
    /**
     * Rendered by a host component rather than by the router (the Data Readiness Wizard
     * modal). The account/program ids arrive as props, save and cancel emit instead of
     * navigating, and the policy table stops writing filters into the host's URL.
     */
    embedded: { required: false, type: Boolean, default: false },
    accountId: { required: false, type: [String, Number], default: undefined },
    programId: { required: false, type: [String, Number], default: undefined },
  },
  emits: ['saved', 'cancel'],
  validations: {
    form: {
      programName: {
        required,
      },
      year: {
        required,
        numeric,
      },
    },
  },
  computed: {
    nameErrors() {
      const errors = [];
      // Server-side rejection, so it stands outside vuelidate's $dirty gate: editing only the
      // policy selection of an existing program can collide without ever touching the name.
      if (this.duplicateNameError) errors.push(this.duplicateNameError);
      if (!this.v$?.form?.programName?.$dirty) {
        return errors;
      }
      if (!this.v$.form?.programName?.required) errors.push('A program name is required.');
      return errors;
    },
    yearErrors() {
      if (!this.v$?.form?.year?.$dirty) {
        return [];
      }
      const errors = [];
      if (!this.v$.form?.year?.required) errors.push('A program year is required.');
      if (this.form.year !== undefined && !this.v$.form?.year?.numeric && isNaN(Number(this.form.year)))
        errors.push('Program year must be a number.');
      return errors;
    },
    resolvedAccountId() {
      return this.accountId ?? this.$route.params.id;
    },
    resolvedProgramId() {
      if (!this.isEditMode) {
        return '';
      }
      return this.programId ?? this.$route.params.programId;
    },
    skeletonColumns() {
      const canSeeAIStatus = IsUserAnAdmin(store.state.user) || IsUserABroker(store.state.user);
      return consolidatedColumnLabels(canSeeAIStatus, ['Selection', 'Coverage Type']);
    },
    allPoliciesSelected() {
      return Object.keys(this.selectedPolicies).length === this.policies.length;
    },
  },
  watch: {
    showError(visible) {
      clearTimeout(this.errorTimeout);
      if (visible) {
        this.errorTimeout = setTimeout(() => {
          this.showError = false;
        }, 6000);
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty) {
      const answer = window.confirm('Do you really want to leave? you have unsaved changes!');
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  methods: {
    async refreshPoliciesWithOptions(options) {
      this.showCurrentOnly = options.showCurrentOnly;
      this.showExternal = options.showExternal;
      this.showWithoutProgramOnly = options.showWithoutProgramOnly;

      this.reloadingPolicies = true;
      try {
        await this.loadPolicies();
      } finally {
        this.reloadingPolicies = false;
      }
    },
    togglePolicy(policy) {
      if (policy.isChildPolicy) {
        return;
      }

      if (this.selectedPolicies[policy.uniqPolicy]) {
        delete this.selectedPolicies[policy.uniqPolicy];
        this.selectedPolicies = { ...this.selectedPolicies };
      } else {
        this.selectedPolicies[policy.uniqPolicy] = policy;
        this.selectedPolicies = { ...this.selectedPolicies };
      }

      this.checkboxState =
        Object.keys(this.selectedPolicies).length === this.policies.length
          ? 'checked'
          : Object.keys(this.selectedPolicies).length > 0
            ? 'indeterminate'
            : '';
    },
    toggleAllPolicies() {
      if (Object.keys(this.selectedPolicies).length !== 0) {
        this.selectedPolicies = {};
        this.checkboxState = '';
      } else {
        for (const policy of this.policies) {
          this.togglePolicy(policy);
        }
        this.checkboxState = 'checked';
      }
    },
    async saveProgram() {
      if (!this.isEditable) {
        return;
      }
      this.v$.$touch();
      if (this.v$.$anyError || this.v$.$invalid) {
        return;
      }
      if (Object.values(this.selectedPolicies).length === 0) {
        return;
      }
      try {
        this.saving = true;
        this.duplicateNameError = '';
        // determine tower type
        // todo: this seems to be tower v1, probably can remove in the future
        let towerType = 'Ungrouped';
        const policies = Object.values(this.selectedPolicies);
        if (!this.isEditMode) {
          await accountService.createProgram(this.resolvedAccountId, {
            name: this.form.programName,
            year: this.form.year,
            towerType,
            PolicyIDs: policies.map((x) => Number(x?.uniqPolicy)),
          });
        } else {
          await accountService.updateProgram(this.resolvedAccountId, this.resolvedProgramId, {
            name: this.form.programName,
            year: this.form.year,
            towerType,
            PolicyIDs: policies.map((x) => Number(x?.uniqPolicy)),
          });
        }
        this.dirty = false;
        if (this.embedded) {
          this.$emit('saved');
        } else {
          this.$router.push(`/account/${this.resolvedAccountId}/programs`);
        }
      } catch (e) {
        // API returns 400 with body "ErrDuplicateEntry" (CAC-5523) when the program name
        // already exists for this account; handleResponse puts the AxiosResponse on e.cause,
        // so the body string is at e.cause.data.
        const code = e?.cause?.data ?? e?.cause;
        if (code === 'ErrDuplicateEntry') {
          // Field-level, like every other duplicate-name form in the app, rather than a
          // floating toast: it's a correction to one input, shown next to that input.
          this.duplicateNameError = 'A program with this name already exists. Please choose a different name.';
          this.revealNameField();
        } else {
          this.error = `An error has occurred. Please try again later!\n\nDetails: ${e}`;
          this.showError = true;
        }
      } finally {
        this.saving = false;
      }
    },
    // Save lives in a pinned footer while the name sits at the top of a long policy table, so
    // a field-level error can land off-screen. Bring it back into view rather than reporting
    // the failure somewhere the field isn't.
    async revealNameField() {
      await this.$nextTick();
      const input = this.$refs.programNameField?.$el?.querySelector('input');
      input?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      input?.focus();
    },
    cancel() {
      if (!this.embedded) {
        this.$router.push(`/account/${this.resolvedAccountId}/programs`);
        return;
      }
      this.$emit('cancel');
    },
    parseRoute() {
      // Copy/merge are route-query driven and unsupported when embedded.
      if (this.embedded) {
        return;
      }
      this.copiedProgramID = this.$route.query.copiedProgramID;
      this.mergedPrograms = this.$route.query.mergedPrograms?.split(',');
    },
    async loadPolicies() {
      this.policies = await accountService.policiesById(this.resolvedAccountId, !this.showCurrentOnly, this.showWithoutProgramOnly, this.showExternal);
    },
    async refreshPolicies() {
      this.loading = true;

      await this.loadPolicies();
      await this.loadCoverageTypes();
      if (this.isEditMode) {
        const details = await accountService.getProgramDetails(this.resolvedAccountId, this.resolvedProgramId);

        if (details.program.type === 'Automated' || details.program.type === 'EPIC') {
          this.isEditable = false;
          this.dirty = false;
          if (this.embedded) {
            this.$emit('cancel');
          } else {
            this.$router.push(`/account/${this.resolvedAccountId}/programs/${details.program.id}`);
          }
        } else {
          this.isEditable = true;
        }

        this.fillForm(details, false);
      }

      if (this.copiedProgramID !== undefined) {
        const details = await accountService.getProgramDetails(this.resolvedAccountId, this.copiedProgramID);
        this.fillForm(details, `Copy of: ${details.program.name}`, false);
      }

      if (this.mergedPrograms !== undefined) {
        const programDetails = [];
        for (let programId of this.mergedPrograms) {
          programDetails.push(await accountService.getProgramDetails(this.resolvedAccountId, programId));
        }

        const policies = Array.from(
          programDetails
            .map((x) => x.policies)
            .flat()
            .reduce((acc, curr) => {
              return acc.set(curr.uniqPolicy, curr);
            }, new Map())
            .values(),
        );

        const programNames = programDetails.map((x) => x.program.name);

        this.fillForm({ program: null, policies }, `Merged from: ${programNames.join(', ')}`, true);
      }

      this.loading = false;
    },
    fillForm(details, name, merged) {
      this.form.programName = name || details.program.name;

      if (!merged) {
        this.form.year = details.program.year;
      }

      for (const policy of details.policies) {
        this.selectedPolicies[policy.uniqPolicy] = policy;
      }
      this.selectedPolicies = { ...this.selectedPolicies };
      this.rerenderKey = this.rerenderKey + 1;
    },
    async loadCoverageTypes() {
      this.coverageTypes = (await referenceService.coverageTypes()).map((x) => x.label);
    },
  },
  mounted() {
    this.parseRoute();
    this.refreshPolicies();
  },
  beforeUnmount() {
    clearTimeout(this.errorTimeout);
  },
};
</script>

<style scoped>
.text-entry {
  display: block;
}
</style>
